'use client';

import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import Image from 'next/image';
import { cn } from '@medizzconnect/shared';
import { useSearch } from '../search/use-search';
import { useFilterStore } from '../filters/filter-store';

interface HeroSectionProps {
  className?: string;
}

/**
 * Hero-Section mit Video-Hintergrund — exaktes V1-Layout.
 * Suchleiste: einzelner Input mit inline Clear- und Such-Button.
 * Schnellfilter-Tags darunter. CTAs rechts versetzt.
 */
export function HeroSection({ className }: HeroSectionProps) {
  const t = useTranslations('home');
  const locale = useLocale();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const { query, setQuery } = useSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const { categoryId, subcategoryName, treatmentName, resetFilters } = useFilterStore();

  // Video-Autoplay: Mobile = 480p (~700 KB), Desktop = 720p (~1.5 MB) – beide optimiert
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    // src erst per JS setzen – verhindert automatischen Browser-Prefetch beim HTML-Parse
    video.src = isDesktop
      ? '/videos/Medizzconnect_Video_Home_desktop.mp4'
      : '/videos/Medizzconnect_Video_Home_mobile.mp4';
    const handleCanPlay = () => setIsVideoLoaded(true);
    video.addEventListener('canplay', handleCanPlay);
    video.load();
    video.play().catch(() => {});
    return () => video.removeEventListener('canplay', handleCanPlay);
  }, []);

  const hasActiveFilters = !!(categoryId || subcategoryName || treatmentName || query);

  const handleClearAll = useCallback(() => {
    setQuery('');
    resetFilters();
  }, [setQuery, resetFilters]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    inputRef.current?.blur();
  }, []);

  return (
    <section
      className={cn(
        'relative isolate overflow-hidden text-white',
        className,
      )}
    >
      {/* ── Hintergrund: Poster als priorisiertes Image für schnelles LCP ── */}
      {/* Next.js <Image priority> erzeugt automatisch <link rel="preload"> im Head */}
      <Image
        src="/videos/optimized/hero-poster.webp"
        alt=""
        fill
        priority
        fetchPriority="high"
        sizes="100vw"
        className="absolute inset-0 -z-10 object-cover"
        aria-hidden="true"
      />
      {/* Video: Mobile = 480p (~700 KB), Desktop = 1080p (~3.8 MB); src via JS gesetzt */}
      <video
        ref={videoRef}
        className={cn(
          'absolute inset-0 -z-10 h-full w-full object-cover transition-opacity duration-1000',
          isVideoLoaded ? 'opacity-100' : 'opacity-0',
        )}
        poster="/videos/optimized/hero-poster.webp"
        autoPlay
        muted
        loop
        playsInline
        preload="none"
      />
      {/* Dunkle Überlagerung */}
      <div className="absolute inset-0 bg-slate-900/60" />
      {/* Dekorativer Kreis */}
      <div className="absolute inset-y-0 right-[-10%] hidden h-[420px] w-[420px] rounded-full bg-white/10 blur-3xl lg:block" aria-hidden="true" />

      {/* ── Inhalt ── */}
      <div className="relative mx-auto flex w-full max-w-6xl flex-col gap-4 px-6 pt-20 pb-16 sm:px-8 sm:pt-24 lg:px-12 lg:pt-48">
        {/* Titel-Block */}
        <div className="flex flex-col gap-4 lg:-ml-8">
          <span className="text-sm font-semibold  tracking-[0.3em] text-white/70">
            {t('hero.eyebrow')}
          </span>
          <h1 className="max-w-3xl text-4xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-4xl">
            {t('hero.title')}
          </h1>
        </div>

        {/* Such-Sektion + CTAs nebeneinander */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:gap-6">
          {/* ── Linke Spalte: Suche + Filter-Tags ── */}
          <div className="flex flex-col gap-3 flex-1 lg:-ml-8" style={{ maxWidth: '500px' }}>
            {/* Suchleiste – V1-Stil: einzelner Input mit inline Buttons */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-2">
              <div className="relative">
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('hero.searchPlaceholder')}
                  className="w-full border border-white/30 bg-white/50 backdrop-blur-sm py-2.5 pl-4 pr-16 rounded-lg text-sm text-slate-800 placeholder:text-slate-500 shadow-sm transition focus:border-white focus:bg-white/70 focus:outline-none"
                  aria-label={t('hero.searchPlaceholder')}
                />

                {/* Clear-Button */}
                {query && (
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="absolute right-12 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-slate-400 text-white hover:bg-slate-500 transition z-10"
                    aria-label="Clear"
                  >
                    <span className="text-xs leading-none">✕</span>
                  </button>
                )}

                {/* Such-Button */}
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-primary-500 p-1.5 text-white hover:bg-primary-600 transition"
                  aria-label="Suchen"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>

              {/* Filter zurücksetzen */}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="self-start text-xs text-white/80 hover:text-white underline transition"
                >
                  {t('hero.clearFilters')}
                </button>
              )}
            </form>

            {/* Schnell-Filter Tags */}
            <div className="flex flex-wrap gap-2 sm:gap-3 mt-1">
              {['Medical Beauty', 'Ayurveda', 'Schlafmedizin'].map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm hover:bg-white/30 transition-colors cursor-pointer sm:px-4 sm:py-2 sm:text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* ── Rechte Spalte: Einladung für Ärzte + CTA ── */}
          <div className="flex flex-col gap-3 lg:ml-auto mt-6 lg:mt-0" style={{ marginTop: 'auto', paddingTop: '56px' }}>
            <p className="text-base font-semibold text-white">
              {t('hero.doctorInvitationTitle')}
            </p>
            <p className="text-sm text-white/80 max-w-xs">
              {t('hero.doctorInvitationText')}
            </p>
            <a
              href={`https://medizzconnect.com/start/${locale}/record?platform=medizz`}
              className="inline-flex items-center justify-center rounded-lg border border-[#00b8b4]/40 px-6 py-3 text-sm font-semibold text-white backdrop-blur shadow-lg transition hover:-translate-y-0.5 hover:border-[#00b8b4] hover:bg-[#00b8b4]/30"
              style={{ backgroundColor: 'rgba(0, 184, 180, 0.25)' }}
            >
              {t('hero.ctaTraining')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
